import { useEffect, useMemo, useState } from "react";
import ReactJson from "react-json-view";
import RadioButtonControlGroup from "../../../../../../shared/RadioButtonControlGroup";
import {
    buildRenderedPreviewModel,
    formatDetailsForRawJsonView,
} from "./notificationPreviewUtils";
import "./NotificationPreview.scss";

const NOTIFICATION_PREVIEW_TAB = Object.freeze({
    RENDERED: "rendered",
    RAW: "raw",
});

/** Tab and panel DOM ids for accessibility (`aria-controls` / `aria-labelledby`). Raw tab truncates long `emailTemplate` values in JSON view. */
const NOTIFICATION_PREVIEW_TAB_IDS = Object.freeze({
    RENDERED_TAB: "notification-details-preview-tab-rendered",
    RAW_TAB: "notification-details-preview-tab-raw",
    RENDERED_PANEL: "notification-details-preview-rendered-panel",
    RAW_PANEL: "notification-details-preview-raw-panel",
});

const NOTIFICATION_PREVIEW_TAB_OPTIONS = [
    {
        value: NOTIFICATION_PREVIEW_TAB.RENDERED,
        label: "Rendered preview",
        id: NOTIFICATION_PREVIEW_TAB_IDS.RENDERED_TAB,
        controls: NOTIFICATION_PREVIEW_TAB_IDS.RENDERED_PANEL,
    },
    {
        value: NOTIFICATION_PREVIEW_TAB.RAW,
        label: "Raw data",
        id: NOTIFICATION_PREVIEW_TAB_IDS.RAW_TAB,
        controls: NOTIFICATION_PREVIEW_TAB_IDS.RAW_PANEL,
    },
];

/**
 * “Notification preview” section: rendered HTML vs raw JSON tabs for loaded notification details.
 * Resets the active tab to Rendered when `notificationId` changes.
 * The **Raw data** tab reflects the API payload as stored (including full `status` strings such as
 * `CORRESPONDENCE_HRP_UPDATE_PENDING` / `CORRESPONDENCE_HRP_UPDATE_ERROR`).
 *
 * @param {object} props
 * @param {string | null} props.notificationId Selected notification row id; drives tab reset on change.
 * @param {Record<string, unknown>} props.details Loaded details payload from `fetchNotificationDetails`.
 * @returns {import('react').JSX.Element}
 */
const NotificationPreview = ({ notificationId, details }) => {
    const [previewTab, setPreviewTab] = useState(
        NOTIFICATION_PREVIEW_TAB.RENDERED,
    );

    useEffect(() => {
        setPreviewTab(NOTIFICATION_PREVIEW_TAB.RENDERED);
    }, [notificationId]);

    const renderedPreview = useMemo(
        () => buildRenderedPreviewModel(details),
        [details],
    );

    const rawJsonSource = useMemo(
        () => formatDetailsForRawJsonView(details),
        [details],
    );

    const typeUpper = String(details?.type ?? "")
        .trim()
        .toUpperCase();

    return (
        <section
            className="notification-details-sheet__section notification-details-sheet__preview-section"
            aria-labelledby="notification-details-preview-heading"
        >
            <h2
                id="notification-details-preview-heading"
                className="notification-details-sheet__section-title"
            >
                Notification preview
            </h2>

            <div className="notification-details-sheet__preview-toolbar">
                <RadioButtonControlGroup
                    role="tablist"
                    ariaLabel="Notification preview format"
                    value={previewTab}
                    onChange={setPreviewTab}
                    options={NOTIFICATION_PREVIEW_TAB_OPTIONS}
                    minCh="17ch"
                />
            </div>

            <div
                id={NOTIFICATION_PREVIEW_TAB_IDS.RENDERED_PANEL}
                role="tabpanel"
                aria-labelledby={NOTIFICATION_PREVIEW_TAB_IDS.RENDERED_TAB}
                hidden={previewTab !== NOTIFICATION_PREVIEW_TAB.RENDERED}
                className="notification-details-sheet__preview-panel"
            >
                <div className="notification-details-sheet__preview-panel-inner notification-details-sheet__preview-panel-inner--rendered">
                    {renderedPreview.variant === "physicalUnavailable" && (
                        <p
                            className="notification-details-sheet__preview-unavailable"
                            role="status"
                        >
                            <>
                                Log in to Zelis to view the rendered preview of{" "}
                                <strong>
                                    {typeUpper === "CORRESPONDENCE"
                                        ? "correspondence"
                                        : "physical"}
                                </strong>{" "}
                                type notifications. To see the raw message data,
                                switch to the <strong>Raw data</strong> tab.
                            </>
                        </p>
                    )}
                    {renderedPreview.variant === "empty" && (
                        <p className="notification-details-sheet__preview-empty">
                            No preview content.
                        </p>
                    )}
                    {renderedPreview.variant === "html" && (
                        <div
                            className="notification-details-sheet__preview-html"
                            dangerouslySetInnerHTML={{
                                __html: renderedPreview.htmlString,
                            }}
                        />
                    )}
                </div>
            </div>

            <div
                id={NOTIFICATION_PREVIEW_TAB_IDS.RAW_PANEL}
                role="tabpanel"
                aria-labelledby={NOTIFICATION_PREVIEW_TAB_IDS.RAW_TAB}
                hidden={previewTab !== NOTIFICATION_PREVIEW_TAB.RAW}
                className="notification-details-sheet__preview-panel"
            >
                <div className="notification-details-sheet__preview-panel-inner notification-details-sheet__preview-panel-inner--raw">
                    {rawJsonSource && <ReactJson src={rawJsonSource} />}
                </div>
            </div>
        </section>
    );
};

export default NotificationPreview;
